import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Get Support form — a simple request form for every user. Name + email auto-fill from the
// logged-in account; issue is a pulldown; description is capped. Submitting emails the request
// to the MSP intake (support@ -> WonderLAN), which creates the ticket and replies by email.
const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const ACCENT = '#EA6F2E';        // complimentary orange (matches the Get Support header button)
const ACCENT_HOVER = '#D45E1F';
const DESC_MAX = 1000;

const FALLBACK_ISSUES = ['Hardware', 'Software', 'Network / Wi-Fi', 'Account / Access', 'Email', 'Phone', 'Printer', 'Other'];

export default function GetSupportForm({ currentUser }) {
  const fullName = (currentUser?.display_name || `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`).trim();
  const [name, setName] = useState(fullName);
  const [email, setEmail] = useState(currentUser?.email || '');
  const [issues, setIssues] = useState(FALLBACK_ISSUES);
  const [issue, setIssue] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(API + '/helpdesk/categories', { headers: hdrs() })
      .then(r => { if (Array.isArray(r.data) && r.data.length) setIssues(r.data.map(c => c.name)); })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!issue || !description.trim() || !name.trim() || !email.trim() || sending) return;
    setSending(true); setError('');
    try {
      await axios.post(API + '/helpdesk/request', { issue, description: description.trim(), name: name.trim(), email: email.trim() }, { headers: hdrs() });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send your request. Please try again.');
    } finally { setSending(false); }
  };

  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 };
  const input = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };

  if (done) return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#DCFCE7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>Request sent</h2>
      <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, margin: '0 0 24px' }}>
        Your request has been sent to IT Support. You'll receive an email at <strong>{email}</strong> with your ticket and next steps.
      </p>
      <button onClick={() => { setDone(false); setIssue(''); setDescription(''); }}
        onMouseEnter={e => e.currentTarget.style.background = ACCENT_HOVER}
        onMouseLeave={e => e.currentTarget.style.background = ACCENT}
        style={{ padding: '10px 20px', background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        Submit another request
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 24px' }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Get Support</h2>
      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 24px' }}>Tell us what you need help with and IT Support will follow up by email.</p>

      <form onSubmit={submit}>
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Full name</label>
          <input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="Your full name" required />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={input} placeholder="you@caamuseum.org" required />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Issue</label>
          <select value={issue} onChange={e => setIssue(e.target.value)} style={input} required>
            <option value="">Select an issue…</option>
            {issues.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={label}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value.slice(0, DESC_MAX))}
            rows={6} maxLength={DESC_MAX} placeholder="Describe the problem — what's happening, any error messages, and where (office/room) if relevant."
            style={{ ...input, resize: 'vertical' }} required />
          <div style={{ textAlign: 'right', fontSize: 11, color: description.length >= DESC_MAX ? '#DC2626' : '#9CA3AF', marginTop: 4 }}>
            {description.length}/{DESC_MAX}
          </div>
        </div>

        {error && <div style={{ color: '#9B2C2C', fontSize: 13, marginBottom: 14 }}>{error}</div>}

        <button type="submit" disabled={sending || !issue || !description.trim() || !name.trim() || !email.trim()}
          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = ACCENT_HOVER; }}
          onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}
          style={{ width: '100%', padding: '12px', background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: (sending || !issue || !description.trim() || !name.trim() || !email.trim()) ? 0.6 : 1 }}>
          {sending ? 'Sending…' : 'Submit as Ticket'}
        </button>
      </form>
    </div>
  );
}
